import { useMemo } from "react";

import Sentry from "@similarweb/sentry-logger";

import { swSettings } from "app/common/services/swSettings";

export const AssetPermissionName = {
    viewAsset: "ViewAsset",
    editAsset: "EditAsset",
    deleteAsset: "DeleteAsset",
    editRoles: "EditRoles",
} as const;

export type AssetPermissionType = (typeof AssetPermissionName)[keyof typeof AssetPermissionName];

export interface IAssetPermissions {
    ownerUserId?: string;
    permissions?: AssetPermissionType[];
}

export interface IObjectWithAssetPermissions {
    assetPermissions?: IAssetPermissions;
}

export class AssetPermissions<T extends IObjectWithAssetPermissions = IObjectWithAssetPermissions> {
    public readonly permissions: IAssetPermissions["permissions"] = [];
    public readonly ownerId: IAssetPermissions["ownerUserId"] = "";

    constructor(public objectItem: T) {
        if (!this.objectItem) {
            return;
        }

        this.permissions = this.objectItem?.assetPermissions?.permissions ?? [];
        this.ownerId = this.objectItem?.assetPermissions?.ownerUserId ?? "";
    }

    public get isOwner(): boolean {
        return this.getCurrentUserId() === this.ownerId;
    }

    public get canEdit(): boolean {
        return this.hasPermission(AssetPermissionName.editAsset);
    }

    public get canDelete(): boolean {
        return this.hasPermission(AssetPermissionName.deleteAsset);
    }

    public get canView(): boolean {
        return this.hasPermission(AssetPermissionName.viewAsset);
    }

    public get canEditRoles(): boolean {
        return this.hasPermission(AssetPermissionName.editRoles);
    }

    public get isShared(): boolean {
        return this.canView && !this.isOwner;
    }

    private hasPermission(permission: AssetPermissionType): boolean {
        return this.isOwner || this.permissions.includes(permission);
    }

    private getCurrentUserId(): string {
        try {
            return swSettings.user.id.toString();
        } catch {
            Sentry.captureException(new Error("Failed to get current user ID"));
            return "";
        }
    }
}

export const useAssetPermissions = <T extends IObjectWithAssetPermissions>(
    objectItem: T | undefined,
): AssetPermissions<T> | null => {
    return useMemo(() => (objectItem ? new AssetPermissions(objectItem) : null), [objectItem]);
};
